export type ProviderDialogFormValues = {
  name: string;
  client_id: string;
  client_secret: string;
  scopes: string[];
};

export type ProviderDialogDescription = {
  kind: "approval" | "oauth" | "form" | "unknown";
  title: string;
  description: string | null;
  permissions: string[];
  details: Array<{ label: string; value: string }>;
  primaryActionLabel: string | null;
  canApprove: boolean;
  canReject: boolean;
  form: {
    connectorName: string;
    scopes: string[];
    redirectUri: string | null;
  } | null;
};

export type ProviderUiActionDescription = {
  ref: string;
  label: string;
  tone: "primary" | "secondary" | "destructive";
  input: Array<{
    name: string;
    label: string;
    type: "text" | "textarea" | "number" | "boolean" | "select";
    options: string[];
  }>;
};

export type ProviderUiDescription = {
  version: 2;
  mirrorKey: string;
  state: string;
  display: Record<string, unknown>;
  actions: ProviderUiActionDescription[];
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function asString(value: unknown, maxLength = 2_000): string | null {
  return typeof value === "string" && value.trim() && value.trim().length <= maxLength
    ? value.trim()
    : null;
}

export function parseProviderDialogDescription(
  value: unknown,
): ProviderDialogDescription | null {
  const dialog = asRecord(value);
  const rawKind = asString(dialog.kind, 40);
  const kind = rawKind === "approval" || rawKind === "oauth" ||
      rawKind === "form" || rawKind === "unknown"
    ? rawKind
    : null;
  const title = asString(dialog.title, 500);
  if (!kind || !title) return null;
  const permissions = Array.isArray(dialog.permissions)
    ? dialog.permissions.slice(0, 20).flatMap((item) => {
      const text = asString(item, 500);
      return text ? [text] : [];
    })
    : [];
  const details = Array.isArray(dialog.details)
    ? dialog.details.slice(0, 8).flatMap((item) => {
      const detail = asRecord(item);
      const label = asString(detail.label, 200);
      const detailValue = asString(detail.value, 500);
      return label && detailValue ? [{ label, value: detailValue }] : [];
    })
    : [];
  const rawForm = asRecord(dialog.form);
  const connectorName = asString(rawForm.connector_name, 160);
  const scopes = Array.isArray(rawForm.scopes)
    ? rawForm.scopes.slice(0, 20).flatMap((item) => {
      const scope = asString(item, 500);
      return scope ? [scope] : [];
    })
    : [];
  return {
    kind,
    title,
    description: asString(dialog.description, 1_000),
    permissions,
    details,
    primaryActionLabel: asString(dialog.primary_action_label, 160),
    canApprove: dialog.can_approve === true,
    canReject: dialog.can_reject === true,
    form: connectorName
      ? {
        connectorName,
        scopes,
        redirectUri: asString(rawForm.redirect_uri, 4_000),
      }
      : null,
  };
}

export function parseProviderUiDescription(value: unknown): ProviderUiDescription | null {
  const panel = asRecord(value);
  if (panel.version !== 2) return null;
  const mirrorKey = asString(panel.mirror_key, 160);
  const state = asString(panel.state, 40);
  const display = asRecord(panel.display);
  if (!mirrorKey || !state || Object.keys(display).length === 0 && !Array.isArray(panel.actions)) return null;
  const actions: ProviderUiActionDescription[] = Array.isArray(panel.actions) ? panel.actions.slice(0, 50).flatMap((item) => {
    const action = asRecord(item);
    const ref = asString(action.ref, 120);
    const label = asString(action.label, 160);
    const tone = action.tone === "primary" || action.tone === "secondary" || action.tone === "destructive"
      ? action.tone : null;
    if (!ref || !label || !tone) return [];
    const fields = Array.isArray(asRecord(action.input).fields)
      ? (asRecord(action.input).fields as unknown[]).slice(0, 50).flatMap((field) => {
        const definition = asRecord(field);
        const name = asString(definition.name, 100);
        const fieldLabel = asString(definition.label, 200);
        const type = definition.type;
        if (!name || !fieldLabel || !["text", "textarea", "number", "boolean", "select"].includes(String(type))) return [];
        const options = Array.isArray(definition.options)
          ? definition.options.slice(0, 50).flatMap((option) => asString(option, 300) ? [asString(option, 300) as string] : [])
          : [];
        return [{ name, label: fieldLabel, type: type as ProviderUiActionDescription["input"][number]["type"], options }];
      }) : [];
    return [{ ref, label, tone, input: fields } as ProviderUiActionDescription];
  }) : [];
  return { version: 2, mirrorKey, state, display, actions };
}
